import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  receptionsAPI,
  invoicesAPI,
  receptionOrdersAPI,
  receptionReportAPI,
  SERVER_URL
} from '../services/api';
import InvoiceModal from './InvoiceModal';
import PurchaseOrderModal from './PurchaseOrderModal';
import PickingModal from './PickingModal'; // Keeping for legacy reference, though workflow changed
import ReceptionReportSearchModal from './ReceptionReportSearchModal';
import IRDetailModal from './IRDetailModal';

import InvoiceViewModal from './InvoiceViewModal';
import { formatDate } from '../utils/dateUtils';
import OCBalancesModal from './OCBalancesModal';
import './ReceptionDetail.css';

function ReceptionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [reception, setReception] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [receptionOrders, setReceptionOrders] = useState([]); // Legacy support
  const [linkedReports, setLinkedReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [alert, setAlert] = useState(null);
  const [activeTab, setActiveTab] = useState('ir'); // Default to IR tab as per new workflow
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showPOModal, setShowPOModal] = useState(false);
  const [showPickingModal, setShowPickingModal] = useState(false);
  const [showIRModal, setShowIRModal] = useState(false);

  const [selectedPO, setSelectedPO] = useState(null);
  const [selectedIR, setSelectedIR] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [showIRDetailModal, setShowIRDetailModal] = useState(false);
  const [showInvoiceViewModal, setShowInvoiceViewModal] = useState(false);

  const [showOCBalancesModal, setShowOCBalancesModal] = useState(false);
  const [selectedBalances, setSelectedBalances] = useState(null);
  const [selectedIRForBalances, setSelectedIRForBalances] = useState(null);
  const [editingDate, setEditingDate] = useState(null); // null = not editing, string = date being edited

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [recRes, invRes, ordersRes] = await Promise.all([
        receptionsAPI.getById(id),
        invoicesAPI.getInvoices(id),
        receptionOrdersAPI.getByReception(id),
      ]);

      setReception(recRes.data);
      setInvoices(invRes.data);
      setReceptionOrders(ordersRes.data);
      setLinkedReports(recRes.data.linked_irs || []);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInvoiceUpload = async (formData) => {
    try {
      if (editingInvoice) {
        // Update existing invoice
        await invoicesAPI.update(editingInvoice.id, formData);
        setAlert({ type: 'success', message: 'Factura actualizada correctamente' });
      } else {
        // Create new invoice
        formData.append('reception_id', id);
        await invoicesAPI.uploadInvoice(formData);
        setAlert({ type: 'success', message: 'Factura subida correctamente' });
      }

      setShowInvoiceModal(false);
      setEditingInvoice(null);
      loadData();
      setTimeout(() => setAlert(null), 3000);
    } catch (err) {
      setAlert({ type: 'error', message: err.message });
      setTimeout(() => setAlert(null), 5000);
    }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (!window.confirm('¿Está seguro de eliminar esta factura?')) {
      return;
    }

    try {
      await invoicesAPI.delete(invoiceId);
      setAlert({ type: 'success', message: 'Factura eliminada correctamente' });
      loadData();
      setTimeout(() => setAlert(null), 3000);
    } catch (err) {
      setAlert({ type: 'error', message: err.message });
      setTimeout(() => setAlert(null), 5000);
    }
  };

  const handleLinkIR = async (irId) => {
    try {
      await invoicesAPI.linkReceptionReport({
        reception_id: parseInt(id),
        ir_id: irId
      });
      setShowIRModal(false);
      setAlert({ type: 'success', message: 'Informe vinculado correctamente' });
      loadData();
      setTimeout(() => setAlert(null), 3000);
    } catch (err) {
      setAlert({ type: 'error', message: err.message });
    }
  };

  const handleShowOCBalances = (ir) => {
    setSelectedBalances(ir.oc_balances);
    setSelectedIRForBalances(ir.irHId);
    setShowOCBalancesModal(true);
  };

  const handleUnlinkIR = async (irId) => {
    if (!window.confirm('¿Desvincular este informe de recepción?')) {
      return;
    }
    try {
      await invoicesAPI.unlinkReceptionReport({
        reception_id: parseInt(id),
        ir_id: irId
      });
      setAlert({ type: 'success', message: 'Informe desvinculado correctamente' });
      loadData();
      setTimeout(() => setAlert(null), 3000);
    } catch (err) {
      setAlert({ type: 'error', message: err.message });
    }
  };

  const formatCurrency = (val) => {
    return new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(val);
  };

  const handleDateSave = async () => {
    if (!editingDate) return;

    const parts = editingDate.split('-');
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    const day = parseInt(parts[2], 10);
    const d = new Date(year, month - 1, day);
    const today = new Date();
    today.setHours(23, 59, 59, 999);

    if (year < 2020 || year > 2030) {
      alert(`⚠️ El año (${year}) parece incorrecto. Por favor revisá la fecha.`);
      return;
    }

    if (month < 1 || month > 12 || day < 1 || day > 31 ||
      d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) {
      alert('⚠️ La fecha no es válida. Por favor revisala.');
      return;
    }

    if (d > today) {
      if (!window.confirm(`⚠️ La fecha (${day}/${month}/${year}) es posterior a hoy. ¿Desea continuar?`)) {
        return;
      }
    }

    try {
      await receptionsAPI.update(id, { reception_date: editingDate.replace(/-/g, '') });
      setEditingDate(null);
      setAlert({ type: 'success', message: 'Fecha actualizada correctamente' });
      loadData();
      setTimeout(() => setAlert(null), 3000);
    } catch (err) {
      setAlert({ type: 'error', message: err.message });
      setTimeout(() => setAlert(null), 5000);
    }
  };

  if (loading) {
    return <div className="loading">Cargando ingreso...</div>;
  }

  if (error || !reception) {
    return (
      <div>
        <div className="alert alert-error">{error || 'Ingreso no encontrado'}</div>
        <Link to="/" className="btn btn-secondary">Volver</Link>
      </div>
    );
  }

  return (
    <div className="reception-detail">
      <div className="header-bar">
        <Link to="/" className="btn btn-secondary">← Volver</Link>
        <h1>Ingreso #{reception.id}</h1>
        <div className="header-actions">
          {/* Actions placeholder */}
        </div>
      </div>

      {alert && (
        <div className={`alert alert-${alert.type}`}>
          {alert.message}
        </div>
      )}

      <div className="reception-info card">
        {/* ... existing info items ... */}
        <div className="info-row">
          <div className="info-item">
            <label>Fecha:</label>
            {editingDate !== null ? (
              <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="date"
                  value={editingDate}
                  onChange={(e) => setEditingDate(e.target.value)}
                  style={{ padding: '4px 8px', border: '1px solid #3498db', borderRadius: '4px' }}
                />
                <button
                  className="btn btn-primary btn-sm"
                  onClick={handleDateSave}
                  title="Guardar fecha"
                >
                  ✓
                </button>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => setEditingDate(null)}
                  title="Cancelar"
                >
                  ✕
                </button>
              </span>
            ) : (
              <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {formatDate(reception.reception_date)}
                <button
                  className="btn btn-sm"
                  onClick={() => {
                    // Convert YYYYMMDD to YYYY-MM-DD for the date input
                    const rd = reception.reception_date || '';
                    const formatted = rd.length === 8
                      ? `${rd.substring(0, 4)}-${rd.substring(4, 6)}-${rd.substring(6, 8)}`
                      : rd;
                    setEditingDate(formatted);
                  }}
                  title="Editar fecha"
                  style={{ padding: '2px 6px', fontSize: '14px', cursor: 'pointer', background: 'none', border: '1px solid #ccc', borderRadius: '4px' }}
                >
                  ✏️
                </button>
              </span>
            )}
          </div>

          <div className="info-item">
            <label>Proveedor:</label>
            <span>{reception.supplier_name || '-'}</span>
          </div>
        </div>
        {reception.comments && (
          <div className="info-item" style={{ marginTop: '15px' }}>
            <label>Comentarios:</label>
            <span>{reception.comments}</span>
          </div>
        )}
      </div>

      {/* Validation Summary Card */}
      <div className="card validation-summary" style={{ marginTop: '20px', borderLeft: '5px solid #ccc' }}>
        <h3 style={{ marginBottom: '15px', color: '#2c3e50' }}>Validación de Valorización</h3>
        <div className="info-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px' }}>
          <div className="info-item">
            <label>Total Informes (Valorizado):</label>
            <span style={{ fontSize: '1.2em', fontWeight: 'bold' }}>
              ${parseFloat(reception.ir_total_value || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
            </span>
          </div>
          <div className="info-item">
            <label>Total Facturas Adjuntas:</label>
            <span style={{ fontSize: '1.2em', fontWeight: 'bold' }}>
              ${parseFloat(reception.total_invoices || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
            </span>
          </div>
          <div className="info-item">
            <label>Diferencia:</label>
            <span style={{
              fontSize: '1.2em',
              fontWeight: 'bold',
              color: Math.abs(parseFloat(reception.ir_total_value || 0) - parseFloat(reception.total_invoices || 0)) < 1 ? '#27ae60' : '#c0392b'
            }}>
              ${(parseFloat(reception.ir_total_value || 0) - parseFloat(reception.total_invoices || 0)).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
            </span>
            {Math.abs(parseFloat(reception.ir_total_value || 0) - parseFloat(reception.total_invoices || 0)) < 1 ? (
              <span className="badge badge-success" style={{ marginLeft: '10px' }}>OK</span>
            ) : (
              <span className="badge badge-danger" style={{ marginLeft: '10px' }}>DIFERENCIA</span>
            )}
          </div>
        </div>
      </div>

      <div className="tabs">
        <button
          className={`tab ${activeTab === 'ir' ? 'active' : ''}`}
          onClick={() => setActiveTab('ir')}
        >
          Informes de Recepción ({linkedReports.length})
        </button>
        <button
          className={`tab ${activeTab === 'invoices' ? 'active' : ''}`}
          onClick={() => setActiveTab('invoices')}
        >
          Facturas ({invoices.length})
        </button>
      </div>

      {activeTab === 'ir' && (
        <div className="tab-content">
          <div className="actions-bar">
            <button className="btn btn-primary" onClick={() => setShowIRModal(true)}>
              + Vincular Informe Existente
            </button>
          </div>

          <div className="card">
            <table className="table">
              <thead>
                <tr>
                  <th>IR ID</th>
                  <th>Fecha</th>
                  <th>Ref. OCs</th>
                  <th>Estado</th>
                  <th className="text-right">Total OCs</th>
                  <th className="text-right">Rec. Acum.</th>
                  <th className="text-right">Imp. Recibido</th>
                  <th className="text-right">Pendiente</th>
                  <th>Usuario</th>
                  <th>Comentario</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {linkedReports.length === 0 ? (
                  <tr>
                    <td colSpan="11" style={{ textAlign: 'center', padding: '40px' }}>
                      No hay informes vinculados.
                    </td>
                  </tr>
                ) : (
                  linkedReports.map((ir) => (
                    <tr key={ir.irHId}>
                      <td>{ir.irHId}</td>
                      <td>{formatDate(ir.irHDate)}</td>
                      <td>
                        {ir.ocs && ir.ocs.length > 0 ? (
                          ir.ocs.map(ocId => (
                            <span key={ocId} className="badge badge-info" style={{ marginRight: '5px', fontSize: '0.85em' }}>
                              OC #{ocId}
                            </span>
                          ))
                        ) : '-'}
                      </td>
                      <td>
                        {ir.status && (
                          <span className={`badge ${ir.status === 'Cumplida' ? 'badge-success' : 'badge-warning'}`}>
                            {ir.status}
                          </span>
                        )}
                      </td>
                      <td className="text-right">
                        <span
                          className="clickable-amount"
                          title="Ver detalle por OC"
                          onClick={() => handleShowOCBalances(ir)}
                          style={{ cursor: 'pointer', textDecoration: 'underline', color: '#007bff' }}
                        >
                          {formatCurrency(ir.total_oc_amount || 0)}
                        </span>
                      </td>
                      <td className="text-right">
                        <span
                          className="clickable-amount"
                          title="Ver detalle por OC"
                          onClick={() => handleShowOCBalances(ir)}
                          style={{ cursor: 'pointer', textDecoration: 'underline', color: '#007bff' }}
                        >
                          {formatCurrency(ir.accumulated_received_amount || 0)}
                        </span>
                      </td>
                      <td className="text-right">
                        {formatCurrency(ir.received_value || 0)}
                      </td>
                      <td className="text-right" style={{ color: (ir.pending_value > 0 ? '#dc3545' : 'inherit') }}>
                        {ir.pending_value > 0 ? formatCurrency(ir.pending_value) : '-'}
                      </td>
                      <td>{ir.irHUser}</td>
                      <td>{ir.irComment}</td>
                      <td>
                        <button
                          className="btn btn-sm btn-info"
                          style={{ marginRight: '5px' }}
                          onClick={() => { setSelectedIR(ir); setShowIRDetailModal(true); }}
                          title="Ver Detalle"
                        >
                          🔍
                        </button>
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => handleUnlinkIR(ir.irHId)}
                        >
                          Desvincular
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'invoices' && (
        <div className="tab-content">
          <div className="actions-bar">
            <button className="btn btn-primary" onClick={() => setShowInvoiceModal(true)}>
              + Agregar Factura
            </button>
          </div>

          <div className="card">
            <table className="table">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Número</th>
                  <th>Tipo</th>
                  <th>Total</th>
                  <th>Archivo</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {invoices.length === 0 ? (
                  <tr>
                    <td colSpan="6" style={{ textAlign: 'center', padding: '40px' }}>
                      No hay facturas. Agrega una para comenzar.
                    </td>
                  </tr>
                ) : (
                  invoices.map((invoice) => (
                    <tr key={invoice.id}>
                      <td>{formatDate(invoice.invoice_date)}</td>
                      <td>{invoice.invoice_number}</td>
                      <td>{invoice.invoice_type}</td>
                      <td>${parseFloat(invoice.total).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</td>
                      <td>
                        {invoice.file_path ? (
                          <a href={`${SERVER_URL}/${invoice.file_path}`} target="_blank" rel="noopener noreferrer">
                            Ver archivo
                          </a>
                        ) : '-'}
                      </td>
                      <td>
                        <button
                          className="btn btn-sm btn-info"
                          style={{ marginRight: '5px' }}
                          onClick={() => { setSelectedInvoice(invoice); setShowInvoiceViewModal(true); }}
                          title="Ver Factura"
                        >
                          🔍
                        </button>
                        <button
                          className="btn btn-sm btn-primary"
                          style={{ marginRight: '5px' }}
                          onClick={() => { setEditingInvoice(invoice); setShowInvoiceModal(true); }}
                          title="Editar Factura"
                        >
                          ✏️
                        </button>
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => handleDeleteInvoice(invoice.id)}
                        >
                          Eliminar
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showInvoiceModal && (
        <InvoiceModal
          onClose={() => { setShowInvoiceModal(false); setEditingInvoice(null); }}
          onSave={handleInvoiceUpload}
          initialData={editingInvoice}
        />
      )}

      {showIRModal && (
        <ReceptionReportSearchModal
          supplierId={reception.supplier_id}
          supplierName={reception.supplier_name}
          onClose={() => setShowIRModal(false)}
          onLink={handleLinkIR}
        />
      )}

      {showIRDetailModal && (
        <IRDetailModal
          ir={selectedIR}
          onClose={() => { setShowIRDetailModal(false); setSelectedIR(null); }}
        />
      )}

      {showInvoiceViewModal && (
        <InvoiceViewModal
          invoice={selectedInvoice}
          onClose={() => { setShowInvoiceViewModal(false); setSelectedInvoice(null); }}
        />
      )}

      {showOCBalancesModal && selectedBalances && (
        <OCBalancesModal
          balances={selectedBalances}
          currentIrId={selectedIRForBalances}
          onClose={() => { setShowOCBalancesModal(false); setSelectedBalances(null); setSelectedIRForBalances(null); }}
        />
      )}
    </div>
  );
}

export default ReceptionDetail;
